/**
 * Cuánto aire lleva el hilo de mensajes según el ancho que tenga disponible.
 *
 * POR QUÉ. Los márgenes del hilo estaban en píxeles fijos: 40 de padding a cada lado más una
 * canaleta de 60 para que las burbujas no llegaran al borde opuesto. En la columna ancha de la
 * bandeja eso está bien y hace que el chat respire. En una ventana acoplada de 340px son 140px
 * —el 41% del ancho— gastados en aire: el texto se estrangula en una columna finita y cada mensaje
 * se vuelve el doble de alto. El mismo problema tiene la bandeja en un celular.
 *
 * 🪤 LA DECISIÓN SE TOMA POR EL ANCHO DEL CONTENEDOR, NO POR EL DE LA PANTALLA. Una consulta a
 * `window` diría "pantalla grande" y dejaría los 140px puestos dentro de una ventana de 340. Lo que
 * importa es el ancho de la caja donde se dibuja el hilo, y eso solo lo sabe el propio contenedor.
 *
 * Va por contexto y no por props porque la canaleta la aplican tres lugares distintos —el hilo, las
 * rachas de mensajes y las filas—: pasarla de la mano por cada uno era garantizar que alguno
 * quedara con el número viejo.
 */

import { createContext, useContext, useEffect, useState } from 'react';

export interface DensidadHilo {
  /** Padding horizontal del contenedor del hilo. */
  aire: number;
  /** Espacio libre del lado opuesto a la burbuja, para que se note de quién es el mensaje. */
  canaleta: number;
  /** Aire vertical entre mensajes seguidos de la misma persona. */
  saltoMismoAutor: number;
  /** Aire vertical cuando cambia el turno. Más grande: es lo que separa una intervención de otra. */
  saltoOtroAutor: number;
  /** Tamaño del texto del mensaje. */
  fuente: number;
  /** Tamaño de la hora y el "editado" al pie de la burbuja. */
  fuenteHora: number;
}

/** Por debajo de esto, el aire deja de ser aire y pasa a ser un estorbo. */
const ANCHO_ANGOSTO = 480;

/*
 * Los números salen de medir un chat real de WhatsApp, no de tantear:
 *
 *   · La burbuja más ancha ocupa ~80-85% del ancho útil. Eso deja una canaleta del lado opuesto
 *     que se ve —se distingue de una ojeada quién habló— sin estrangular el texto.
 *   · El aire de los costados es chico: lo que separa el mensaje del borde, no una columna.
 *   · Verticalmente, los mensajes seguidos de la misma persona van casi pegados y el cambio de
 *     turno abre. Sin esa diferencia, una conversación se lee como una lista.
 *   · La tipografía baja un punto SOLO en la ventana angosta, y no por gusto: lo que hace cómodo
 *     leer es cuántos caracteres entran por renglón. A 14px en una ventana de 340 entran ~38, por
 *     debajo del rango cómodo (45-75); a 13px entran ~41, más cerca. En la columna ancha de la
 *     bandeja, 14px ya da ~60 —justo en el medio del rango—, así que achicarla ahí lo empeoraría.
 *
 * Las dos densidades mantienen la MISMA proporción de burbuja (~80%): en la columna de la bandeja
 * (~700px) queda 700-80-60=560, y en una ventana acoplada de 340 queda 340-24-40=276. Por eso los
 * números no son "grande y chico" al azar — son la misma decisión a dos escalas.
 */
const HOLGADO: DensidadHilo = { aire: 40, canaleta: 60, saltoMismoAutor: 2, saltoOtroAutor: 10, fuente: 14, fuenteHora: 11 };
const COMPACTO: DensidadHilo = { aire: 12, canaleta: 40, saltoMismoAutor: 2, saltoOtroAutor: 8, fuente: 13, fuenteHora: 10.5 };

const Ctx = createContext<DensidadHilo>(HOLGADO);

export const DensidadHiloProvider = Ctx.Provider;

export function useDensidadHilo(): DensidadHilo {
  return useContext(Ctx);
}

/**
 * Observa el ancho real del elemento y devuelve la densidad que le corresponde.
 *
 * Arranca en holgado a propósito: es lo que ya se veía, así que en el peor caso —un navegador sin
 * ResizeObserver— nada empeora respecto de antes.
 */
export function useDensidadPorAncho<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
): DensidadHilo {
  const [densidad, setDensidad] = useState<DensidadHilo>(HOLGADO);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observador = new ResizeObserver(([entrada]) => {
      const ancho = entrada.contentRect.width;
      // Comparar contra el valor anterior evita re-renderizar el hilo entero en cada píxel que se
      // mueve al arrastrar el borde de la ventana.
      setDensidad((prev) => {
        const siguiente = ancho > 0 && ancho < ANCHO_ANGOSTO ? COMPACTO : HOLGADO;
        return prev.aire === siguiente.aire ? prev : siguiente;
      });
    });
    observador.observe(el);
    return () => observador.disconnect();
  }, [ref]);

  return densidad;
}
